import { TFunction } from "i18next";
import { Subrecipe, SubrecipeIngredient } from "../../../../services/subrecipes/subrecipesService";
import { CustomIngredient } from "../../../../services/customIngredients/customIngredientsService";
import { OrderHistory } from "../../../../stores/appOrderHistoryStore";
import {
    calculateCurrentPrice,
    calculateSubrecipeUnitCost,
    SUBRECIPE_MAX_DEPTH,
} from "../../../../utils/costPriceUtils";
import { applyWaste, resolveWaste } from "../../../../utils/wasteUtils";
import { getPortionsUnitDisplay } from "../../../../utils/ingredientGrouping";
import { getProductPlatformDisplay } from "./CostPrice/useCostPriceDetail";
import { escapeCsvCell } from "./costPriceExport";

/**
 * Subrecipe exports for the Manage Ingredients page (CSV + readable .txt), plus
 * the subrecipe-contents walk and cell formatters shared with the detailed cost
 * price export (`costPriceDetailedExport.ts`) — one implementation, so a
 * subrecipe's lines cost the same in every export.
 *
 * The subrecipes handed in are exactly the ones the page is showing (search
 * already applied) — the export never re-derives or re-filters data.
 */

// --- cell formatting (shared with costPriceDetailedExport) -------------------

/** Money to 2 decimals; blank when there is no number to show. */
export const money = (value?: number): string =>
    value === undefined || value === null || !Number.isFinite(value) ? '' : value.toFixed(2);

/** Unit prices need more precision than money — €0.0321/g rounds to €0.03 otherwise. */
export const unitPrice = (value?: number): string =>
    value === undefined || value === null || !Number.isFinite(value) ? '' : value.toFixed(4);

/** Quantities: up to 4 decimals, trailing zeros trimmed (0.1875, 80, 3.75). */
export const quantity = (value?: number): string =>
    value === undefined || value === null || !Number.isFinite(value) ? '' : String(Number(value.toFixed(4)));

/** Two spaces per nesting level — `Type` stays the machine-readable signal. */
export const indent = (level: number, name: string): string => `${'  '.repeat(level)}${name}`;

export const supplierOf = (platform?: string, customPlatformName?: string): string =>
    getProductPlatformDisplay(platform || '', customPlatformName).displayName;

// --- the shared subrecipe walk -----------------------------------------------

export type SubrecipeLineType = 'ingredient' | 'subrecipe' | 'alternative';

/** One line of a subrecipe's contents, format-neutral (labels applied by consumers). */
export interface SubrecipeLine {
    type: SubrecipeLineType;
    /** Empty with `deleted: true` when a nested reference points at a deleted subrecipe. */
    name: string;
    deleted?: boolean;
    /** Nesting level relative to the walked subrecipe (0 = direct content). */
    level: number;
    supplier?: string;
    qty?: number;
    unit?: string;
    waste?: number;
    /** Price of ONE `unit`; alternatives deliberately carry none (see detailed export). */
    price?: number;
    cost?: number;
}

/** The data set a subrecipe's ingredients are priced against. */
export interface SubrecipeWalkData {
    orders: OrderHistory[];
    custom?: CustomIngredient[];
    subrecipes: Map<string, Subrecipe>;
}

/**
 * The contents of a subrecipe as export lines, quantities multiplied by `scale`
 * (1 = the recipe as entered; the detailed export passes the per-dish scale).
 * Prices are looked up live with the exact lookup `ingredientCostFromSubrecipeRow`
 * uses, so ingredient lines sum to the subrecipe's unit cost × portions.
 */
export function walkSubrecipeLines(
    subrecipe: Subrecipe,
    scale: number,
    level: number,
    data: SubrecipeWalkData,
    showExVat: boolean,
    defaultWastePercentage: number,
    out: SubrecipeLine[],
    depth: number,
    visited: Set<string>
): void {
    if (depth >= SUBRECIPE_MAX_DEPTH) return;

    for (const ing of subrecipe.ingredients as SubrecipeIngredient[]) {
        if (ing.nestedSubrecipeId) {
            const child = data.subrecipes.get(ing.nestedSubrecipeId);
            const portionsNeeded = (ing.nestedSubrecipePortionsNeeded ?? 0) * scale;
            const childUnitCost = child && !visited.has(child.id)
                ? calculateSubrecipeUnitCost(
                    child, data.orders, data.custom, showExVat, data.subrecipes,
                    defaultWastePercentage, depth + 1, visited
                )
                : undefined;
            out.push({
                type: 'subrecipe',
                name: child?.name ?? '',
                deleted: !child,
                level,
                qty: portionsNeeded,
                unit: child ? getPortionsUnitDisplay(child.portionsUnit) : '',
                price: childUnitCost,
                cost: childUnitCost === undefined ? undefined : childUnitCost * portionsNeeded,
            });
            if (child && child.portions && !visited.has(child.id)) {
                const nextVisited = new Set(visited);
                nextVisited.add(child.id);
                walkSubrecipeLines(
                    child, portionsNeeded / child.portions, level + 1, data,
                    showExVat, defaultWastePercentage, out, depth + 1, nextVisited
                );
            }
            continue;
        }

        const qty = (ing.quantityNeeded ?? 0) * scale;
        const waste = resolveWaste(ing.wastePercentage, defaultWastePercentage);
        // Same guard as `ingredientCostFromSubrecipeRow`: an incomplete row has no
        // resolvable price, but the line is still listed so the recipe is complete.
        const price = ing.articleId && ing.platform && ing.quantityNeeded !== undefined
            ? calculateCurrentPrice(
                {
                    id: ing.id,
                    articleId: ing.articleId,
                    productName: ing.productName ?? '',
                    quantityNeeded: ing.quantityNeeded,
                    quantityInfo: ing.quantityInfo ?? { stdAmount: 0, stdMultiplier: 0, stdUnit: '' },
                    platform: ing.platform,
                    customPlatformName: ing.customPlatformName,
                    custom: ing.isCustom,
                },
                data.orders,
                data.custom,
                showExVat
            ).currentPrice
            : undefined;

        out.push({
            type: 'ingredient',
            name: ing.productName ?? '',
            level,
            supplier: supplierOf(ing.platform, ing.customPlatformName),
            qty,
            unit: ing.quantityInfo?.stdUnit ?? '',
            waste,
            price,
            cost: price === undefined ? undefined : applyWaste(qty, waste) * price,
        });

        // Alternatives: quantity + waste only, never priced (they only feed the
        // waste-allocation engine; a cost would double-count the ingredient's).
        for (const alt of ing.alternatives ?? []) {
            out.push({
                type: 'alternative',
                name: alt.productName,
                level: level + 1,
                supplier: supplierOf(alt.platform, alt.customPlatformName),
                qty: (alt.quantityNeeded ?? 0) * scale,
                unit: alt.quantityInfo?.stdUnit ?? '',
                waste: resolveWaste(alt.wastePercentage, defaultWastePercentage),
            });
        }
    }
}

// --- Manage Ingredients page exports -----------------------------------------

export interface SubrecipesExportContext {
    orderHistory: OrderHistory[];
    customIngredients?: CustomIngredient[];
    subrecipesById: Map<string, Subrecipe>;
    showExVat: boolean;
    defaultWastePercentage: number;
    restaurantName: string;
    generatedAt: Date;
}

const lineTypeLabel = (t: TFunction, type: SubrecipeLineType): string => {
    if (type === 'subrecipe') return t('costPrice.export.detailed.typeSubrecipe');
    if (type === 'alternative') return t('costPrice.export.detailed.typeAlternative');
    return t('costPrice.export.detailed.typeIngredient');
};

const lineName = (t: TFunction, line: SubrecipeLine): string =>
    line.deleted ? t('costPrice.subrecipes.deletedSubrecipeLabel') : line.name;

/** Unit cost + contents lines of one subrecipe, as every export consumes them. */
function subrecipeLines(subrecipe: Subrecipe, ctx: SubrecipesExportContext): { unitCost: number; lines: SubrecipeLine[] } {
    const unitCost = calculateSubrecipeUnitCost(
        subrecipe, ctx.orderHistory, ctx.customIngredients, ctx.showExVat,
        ctx.subrecipesById, ctx.defaultWastePercentage
    );
    const lines: SubrecipeLine[] = [];
    walkSubrecipeLines(
        subrecipe, 1, 0,
        { orders: ctx.orderHistory, custom: ctx.customIngredients, subrecipes: ctx.subrecipesById },
        ctx.showExVat, ctx.defaultWastePercentage, lines, 0, new Set([subrecipe.id])
    );
    return { unitCost, lines };
}

/**
 * CSV: one line per subrecipe (yield in the Quantity/Unit cells, unit cost as
 * `Unit price`, total as `Cost` — so `Cost = Quantity × Unit price` reads the
 * same as on detail lines), followed by its contents underneath, one line per
 * ingredient / nested subrecipe / alternative, quantities for the full batch.
 */
export function buildSubrecipesCsv(subrecipes: Subrecipe[], t: TFunction, ctx: SubrecipesExportContext): string {
    const headers = [
        t('costPrice.export.detailed.type'),
        t('costPrice.export.detailed.name'),
        t('costPrice.subrecipesExport.description'),
        t('costPrice.export.detailed.supplier'),
        t('costPrice.export.detailed.quantity'),
        t('costPrice.export.detailed.unit'),
        t('costPrice.export.detailed.waste'),
        t('costPrice.export.detailed.unitPrice'),
        t('costPrice.table.cost'),
    ];
    const lines = [headers.map(escapeCsvCell).join(',')];

    for (const subrecipe of subrecipes) {
        const { unitCost, lines: contents } = subrecipeLines(subrecipe, ctx);
        lines.push([
            t('costPrice.export.detailed.typeSubrecipe'),
            subrecipe.name,
            subrecipe.description ?? '',
            '',
            quantity(subrecipe.portions),
            getPortionsUnitDisplay(subrecipe.portionsUnit),
            '',
            unitPrice(unitCost),
            money(unitCost * subrecipe.portions),
        ].map(escapeCsvCell).join(','));

        for (const line of contents) {
            lines.push([
                lineTypeLabel(t, line.type),
                indent(line.level + 1, lineName(t, line)),
                '',
                line.supplier ?? '',
                quantity(line.qty),
                line.unit ?? '',
                quantity(line.waste),
                unitPrice(line.price),
                money(line.cost),
            ].map(escapeCsvCell).join(','));
        }
    }

    return lines.join('\n');
}

/**
 * Readable .txt (markdown-ish, same spirit as the revenue items export): a small
 * metadata block, then one section per subrecipe — name, description, yield and
 * total cost, followed by the indented contents list. Line parts are joined with
 * " — " and blank parts (e.g. a missing price) are simply left out.
 */
export function buildSubrecipesTxt(subrecipes: Subrecipe[], t: TFunction, ctx: SubrecipesExportContext): string {
    const parts: string[] = [];
    const title = t('costPrice.manageIngredients.subrecipes');
    parts.push(ctx.restaurantName ? `# ${title} — ${ctx.restaurantName}` : `# ${title}`);
    parts.push('');
    parts.push(`- ${title}: ${subrecipes.length}`);
    parts.push(`- ${t('costPrice.subrecipesExport.vat')}: ${ctx.showExVat ? t('costPrice.subrecipesExport.vatExcl') : t('costPrice.subrecipesExport.vatIncl')}`);
    parts.push(`- ${t('costPrice.subrecipesExport.generated')}: ${ctx.generatedAt.toLocaleString()}`);

    for (const subrecipe of subrecipes) {
        const { unitCost, lines: contents } = subrecipeLines(subrecipe, ctx);
        const yieldUnit = getPortionsUnitDisplay(subrecipe.portionsUnit);
        parts.push('');
        parts.push(`## ${subrecipe.name}`);
        if (subrecipe.description) parts.push(subrecipe.description);
        parts.push(`- ${t('costPrice.subrecipesExport.yield')}: ${quantity(subrecipe.portions)} ${yieldUnit}`);
        parts.push(`- ${t('costPrice.subrecipesExport.totalCost')}: €${money(unitCost * subrecipe.portions)} (€${unitPrice(unitCost)}/${yieldUnit})`);
        parts.push('');

        for (const line of contents) {
            const pieces: string[] = [];
            const tag = line.type === 'subrecipe'
                ? ` ${t('costPrice.subrecipes.subrecipeTag')}`
                : '';
            const prefix = line.type === 'alternative'
                ? `${t('costPrice.export.detailed.typeAlternative')}: `
                : '';
            pieces.push(`${prefix}${lineName(t, line)}${tag}`);
            if (line.supplier) pieces.push(line.supplier);
            if (line.qty !== undefined) pieces.push(`${quantity(line.qty)} ${line.unit ?? ''}`.trim());
            if (line.waste !== undefined && line.waste > 0) {
                pieces.push(`${t('costPrice.subrecipesExport.waste')} ${quantity(line.waste)}%`);
            }
            if (line.price !== undefined && Number.isFinite(line.price)) {
                pieces.push(`€${unitPrice(line.price)}${line.unit ? `/${line.unit}` : ''}`);
            }
            if (line.cost !== undefined && Number.isFinite(line.cost)) {
                pieces.push(`€${money(line.cost)}`);
            }
            parts.push(indent(line.level + 1, `- ${pieces.join(' — ')}`));
        }
    }

    return parts.join('\n');
}

/** Filename: subrecipes-<restaurant-slug>-<yyyy-mm-dd>.<ext> */
export function subrecipesExportFilename(restaurantName: string, extension: 'csv' | 'txt', generatedAt: Date): string {
    const slug = restaurantName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const stamp = generatedAt.toISOString().slice(0, 10);
    return ['subrecipes', slug || null, stamp].filter(Boolean).join('-') + `.${extension}`;
}
